import {
	webhookEventTypes,
	type MembershipSummary,
	type WebhookDeliverySummary,
	type WebhookEndpointSummary,
} from '@livechat/shared';
import { type FormEvent, useEffect, useState } from 'react';

import {
	createWebhook,
	listWebhookDeliveries,
	listWebhooks,
	rotateWebhookSecret,
	updateWebhook,
} from '../api.js';

export function TenantWebhooks({ membership }: { membership: MembershipSummary }) {
	const [webhooks, setWebhooks] = useState<WebhookEndpointSummary[]>([]);
	const [deliveries, setDeliveries] = useState<WebhookDeliverySummary[]>([]);
	const [selectedEndpointId, setSelectedEndpointId] = useState<string>();
	const [signingSecret, setSigningSecret] = useState<string>();
	const [showCreate, setShowCreate] = useState(false);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string>();

	useEffect(() => {
		setError(undefined);
		void listWebhooks(membership.tenantId)
			.then(setWebhooks)
			.catch((caught: unknown) =>
				setError(caught instanceof Error ? caught.message : 'Could not load webhooks'),
			);
	}, [membership.tenantId]);

	async function create(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		setBusy(true);
		setError(undefined);
		const form = event.currentTarget;
		const data = new FormData(form);
		const eventTypes = webhookEventTypes.filter((eventType) => data.has(eventType));
		try {
			const created = await createWebhook(membership.tenantId, {
				description: String(data.get('description')),
				eventTypes: eventTypes.length ? eventTypes : ['*'],
				url: String(data.get('url')),
			});
			setWebhooks((current) => [...current, created.endpoint]);
			setSigningSecret(created.signingSecret);
			setShowCreate(false);
			form.reset();
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not create the webhook');
		} finally {
			setBusy(false);
		}
	}

	async function changeEndpoint(
		endpoint: WebhookEndpointSummary,
		input: Partial<WebhookEndpointSummary>,
	): Promise<void> {
		setBusy(true);
		setError(undefined);
		try {
			const changed = await updateWebhook(membership.tenantId, endpoint.id, input);
			setWebhooks((current) =>
				current.map((item) => (item.id === changed.id ? changed : item)),
			);
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not update the webhook');
		} finally {
			setBusy(false);
		}
	}

	async function rotate(endpointId: string): Promise<void> {
		if (!window.confirm('Rotate this signing secret? The previous secret will stop working.')) {
			return;
		}
		setBusy(true);
		try {
			const rotated = await rotateWebhookSecret(membership.tenantId, endpointId);
			setSigningSecret(rotated.signingSecret);
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not rotate the secret');
		} finally {
			setBusy(false);
		}
	}

	async function inspectDeliveries(endpointId: string): Promise<void> {
		setSelectedEndpointId(endpointId);
		try {
			setDeliveries(await listWebhookDeliveries(membership.tenantId, endpointId));
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not load deliveries');
		}
	}

	return (
		<div className="operations-grid">
			{error && <div className="alert error operations-span">{error}</div>}
			<section className="operations-card">
				<div className="subsection-heading compact-heading">
					<div>
						<h3>Webhooks</h3>
						<p>Send signed conversation events to your systems.</p>
					</div>
					<button
						className="primary-button"
						onClick={() => setShowCreate((value) => !value)}
					>
						{showCreate ? 'Cancel' : 'Add endpoint'}
					</button>
				</div>

				{signingSecret && (
					<div className="secret-result">
						<strong>Copy this signing secret now</strong>
						<p>It is encrypted at rest and will not be displayed again.</p>
						<div>
							<code>{signingSecret}</code>
							<button
								onClick={() => void navigator.clipboard.writeText(signingSecret)}
							>
								Copy
							</button>
						</div>
					</div>
				)}

				{showCreate && (
					<form className="webhook-form" onSubmit={(event) => void create(event)}>
						<label>
							<span>Description</span>
							<input
								name="description"
								maxLength={240}
								placeholder="CRM event receiver"
							/>
						</label>
						<label>
							<span>HTTPS endpoint</span>
							<input
								name="url"
								type="url"
								required
								placeholder="https://example.com/hooks/livechat"
							/>
						</label>
						<fieldset>
							<legend>Events (none selected sends all)</legend>
							{webhookEventTypes.map((eventType) => (
								<label key={eventType}>
									<input name={eventType} type="checkbox" /> {eventType}
								</label>
							))}
						</fieldset>
						<button className="primary-button" disabled={busy}>
							Create endpoint
						</button>
					</form>
				)}

				<div className="webhook-list">
					{webhooks.map((endpoint) => (
						<article className="webhook-row" key={endpoint.id}>
							<div>
								<span className={`status-pill ${endpoint.status}`}>
									{endpoint.status}
								</span>
								<strong>{endpoint.description || 'Webhook endpoint'}</strong>
								<code>{endpoint.url}</code>
								<small>
									{endpoint.eventTypes.join(', ')} · {endpoint.failureCount}{' '}
									recent failures
								</small>
							</div>
							<div className="webhook-actions">
								<button onClick={() => void inspectDeliveries(endpoint.id)}>
									Deliveries
								</button>
								<button disabled={busy} onClick={() => void rotate(endpoint.id)}>
									Rotate secret
								</button>
								<button
									disabled={busy}
									onClick={() =>
										void changeEndpoint(endpoint, {
											status:
												endpoint.status === 'active'
													? 'disabled'
													: 'active',
										})
									}
								>
									{endpoint.status === 'active' ? 'Disable' : 'Enable'}
								</button>
							</div>
						</article>
					))}
					{!webhooks.length && (
						<div className="operations-empty">No webhook endpoints configured.</div>
					)}
				</div>

				{selectedEndpointId && (
					<div className="delivery-list">
						<h4>Recent deliveries</h4>
						{deliveries.map((delivery) => (
							<div key={delivery.id}>
								<span className={`status-pill ${delivery.status}`}>
									{delivery.status}
								</span>
								<code>{delivery.eventType}</code>
								<span>
									{delivery.responseStatus
										? `HTTP ${delivery.responseStatus}`
										: 'No response yet'}{' '}
									· attempt {delivery.attemptCount}
								</span>
							</div>
						))}
						{!deliveries.length && <p>No deliveries yet.</p>}
					</div>
				)}
			</section>
		</div>
	);
}
